import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Video, 
  Plus, 
  Calendar,
  Clock,
  Users,
  ExternalLink,
  Trash2,
  FileText,
  Loader2,
  Copy,
  Check,
  Play,
  Sparkles,
  Mail,
  Send,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

export default function MeetingsPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [todayMeetings, setTodayMeetings] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmails, setInviteEmails] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [staff, setStaff] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '10:00',
    attendees: [],
    meeting_type: 'staff'
  });
  
  const [canCreateMeeting, setCanCreateMeeting] = useState(false);
  const [showReportsDialog, setShowReportsDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [meetingToManage, setMeetingToManage] = useState(null);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [reports, setReports] = useState(null);
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  useEffect(() => {
    fetchData();
    checkPermissions();
  }, []);
  
  const checkPermissions = async () => {
    try {
      const response = await api.get('/meetings/check-create-permission');
      setCanCreateMeeting(response.data.can_create);
    } catch (error) {
      setCanCreateMeeting(false);
    }
  };

  const fetchData = async () => {
    try {
      const [meetingsRes, todayRes, upcomingRes, staffRes] = await Promise.all([
        api.get('/meetings/list'),
        api.get('/meetings/today'),
        api.get('/meetings/upcoming'),
        api.get('/staff')
      ]);
      
      setMeetings(meetingsRes.data.meetings || []);
      setTodayMeetings(todayRes.data.meetings || []);
      setUpcomingMeetings(upcomingRes.data.meetings || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReports = async () => {
    try {
      const response = await api.get('/meetings/reports/summary');
      setReports(response.data);
      setShowReportsDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch reports');
    }
  };
  
  const openBlockDialog = async (meeting) => {
    setMeetingToManage(meeting);
    try {
      const response = await api.get(`/meetings/${meeting.id}/participants`);
      setMeetingParticipants(response.data);
      setShowBlockDialog(true);
    } catch (error) {
      toast.error('Failed to fetch participants');
    }
  };
  
  const blockUser = async (userId) => {
    if (!window.confirm('Are you sure you want to block this user from the meeting?')) return;
    
    try {
      await api.post(`/meetings/${meetingToManage.id}/block-user`, { user_id: userId });
      toast.success('User blocked from meeting');
      // Refresh participants
      const response = await api.get(`/meetings/${meetingToManage.id}/participants`);
      setMeetingParticipants(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };
  
  const unblockUser = async (userId) => {
    try {
      await api.post(`/meetings/${meetingToManage.id}/unblock-user`, { user_id: userId });
      toast.success('User unblocked');
      // Refresh participants
      const response = await api.get(`/meetings/${meetingToManage.id}/participants`);
      setMeetingParticipants(response.data);
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const createMeeting = async () => {
    if (!newMeeting.title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }
    
    try {
      const response = await api.post('/meetings/create', newMeeting);
      toast.success('Meeting created!');
      setShowNewDialog(false);
      fetchData();
      
      // Offer to join immediately
      if (window.confirm('Meeting created. Join now?')) {
        navigate(`/meeting-room/${response.data.room_code}`);
      }
      
      setNewMeeting({
        title: '',
        description: '',
        scheduled_date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '10:00',
        attendees: [],
        meeting_type: 'staff'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    
    try {
      await api.delete(`/meetings/${id}`);
      toast.success('Meeting deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const openInviteDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setInviteEmails([]);
    setInviteEmail('');
    setShowInviteDialog(true);
  };

  const addEmailToInvite = () => {
    if (inviteEmail && !inviteEmails.includes(inviteEmail)) {
      // Basic email validation
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
        setInviteEmails([...inviteEmails, inviteEmail]);
        setInviteEmail('');
      } else {
        toast.error('Please enter a valid email address');
      }
    }
  };

  const removeEmailFromInvite = (email) => {
    setInviteEmails(inviteEmails.filter(e => e !== email));
  };

  const sendMeetingInvites = async () => {
    if (inviteEmails.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    setSendingInvite(true);
    try {
      await api.post(`/meetings/${selectedMeeting.id}/send-invites`, {
        emails: inviteEmails
      });
      toast.success(`Invitations sent to ${inviteEmails.length} recipient(s)!`);
      setShowInviteDialog(false);
      setInviteEmails([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSendingInvite(false);
    }
  };

  const openEmailClient = (meeting) => {
    const meetingUrl = `${window.location.origin}/meeting-room/${meeting.room_code}`;
    const subject = encodeURIComponent(`Meeting Invitation: ${meeting.title}`);
    const body = encodeURIComponent(`You are invited to a meeting.

Meeting Details:
- Title: ${meeting.title}
- Date: ${meeting.scheduled_date}
- Time: ${meeting.start_time} - ${meeting.end_time}
- Type: ${meeting.meeting_type}

Join the meeting using this link:
${meetingUrl}

Room Code: ${meeting.room_code}

Please log in to your ICRCL account to join the meeting.

Best regards,
${user?.first_name} ${user?.last_name}
ICRCL - Innovative Care and Rehab Centre Limited`);
    
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const shareViaWhatsApp = (meeting) => {
    const meetingUrl = `${window.location.origin}/meeting-room/${meeting.room_code}`;
    const message = encodeURIComponent(`*Meeting Invitation: ${meeting.title}*

📅 Date: ${meeting.scheduled_date}
🕐 Time: ${meeting.start_time} - ${meeting.end_time}

🔗 Join here: ${meetingUrl}

Room Code: ${meeting.room_code}`);
    
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const copyRoomCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(code);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-green-100 text-green-700 animate-pulse',
      completed: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || styles.scheduled}>{status.replace('_', ' ')}</Badge>;
  };

  const MeetingCard = ({ meeting, showDate = true }) => (
    <Card className="card-icrcl hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-[#1e3a5f] truncate">{meeting.title}</h3>
              {getStatusBadge(meeting.status)}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-2">
              {showDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {meeting.scheduled_date}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {meeting.start_time} - {meeting.end_time}
              </span>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-slate-100 px-2 py-1 rounded">{meeting.room_code}</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => copyRoomCode(meeting.room_code)}
                title="Copy room code"
              >
                {copiedId === meeting.room_code ? (
                  <Check className="w-3 h-3 text-green-500" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </Button>
              
              {/* Send Invite Options */}
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                onClick={() => openInviteDialog(meeting)}
                title="Send meeting invite via email"
              >
                <Mail className="w-3 h-3 mr-1 text-blue-500" />
                Send Invite
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs bg-green-50 hover:bg-green-100 border-green-200"
                onClick={() => shareViaWhatsApp(meeting)}
                title="Share via WhatsApp"
              >
                <MessageSquare className="w-3 h-3 mr-1 text-green-500" />
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs"
                onClick={() => {
                  const meetingUrl = `${window.location.origin}/meeting-room/${meeting.room_code}`;
                  navigator.clipboard.writeText(meetingUrl);
                  toast.success('Meeting link copied! Share it with attendees.');
                }}
                title="Copy shareable meeting link"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Copy Link
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            {meeting.status !== 'completed' && meeting.status !== 'cancelled' && (
              <Button
                size="sm"
                className="bg-[#2a9d8f] hover:bg-[#2a9d8f]/90"
                onClick={() => navigate(`/meeting-room/${meeting.room_code}`)}
              >
                <Play className="w-4 h-4 mr-1" />
                Join
              </Button>
            )}
            {/* AI Minutes Button - Always visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/meetings/${meeting.id}/minutes`)}
              className="bg-amber-50 hover:bg-amber-100 border-amber-200"
              title="Generate AI Meeting Minutes"
            >
              <Sparkles className="w-4 h-4 mr-1 text-amber-500" />
              AI Minutes
            </Button>
            {meeting.minutes && (
              <Badge className="bg-green-100 text-green-700">Has Minutes</Badge>
            )}
            {/* Block Users - Super Admin Only */}
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openBlockDialog(meeting)}
                className="bg-red-50 hover:bg-red-100 border-red-200"
                title="Manage blocked users"
              >
                <Users className="w-4 h-4 mr-1 text-red-500" />
                Manage Access
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-red-500"
              onClick={() => deleteMeeting(meeting.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Video className="w-8 h-8 text-[#2a9d8f]" />
            Video Meetings
          </h1>
          <p className="text-slate-500 mt-1">Schedule and manage video meetings with staff and parents</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Reports Button - Directors and Super Admin */}
          <Button 
            variant="outline"
            onClick={fetchReports}
            className="border-[#1e3a5f] text-[#1e3a5f]"
          >
            <FileText className="w-4 h-4 mr-2" />
            View Reports
          </Button>
          
          {/* New Meeting - Only for authorized users */}
          {canCreateMeeting && (
            <Button 
              onClick={() => setShowNewDialog(true)} 
              className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
              data-testid="new-meeting-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Meeting
            </Button>
          )}
        </div>
      </div>

      {/* Today's Meetings */}
      {todayMeetings.length > 0 && (
        <Card className="card-icrcl border-l-4 border-l-[#2a9d8f]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2a9d8f] rounded-full animate-pulse" />
              Today's Meetings
            </CardTitle>
            <CardDescription>{todayMeetings.length} meeting(s) scheduled for today</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} showDate={false} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabs for All Meetings */}
      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming ({upcomingMeetings.length})</TabsTrigger>
          <TabsTrigger value="all">All Meetings ({meetings.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="space-y-3 mt-4">
          {upcomingMeetings.length === 0 ? (
            <Card className="card-icrcl">
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No upcoming meetings</p>
              </CardContent>
            </Card>
          ) : (
            upcomingMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))
          )}
        </TabsContent>
        
        <TabsContent value="all" className="space-y-3 mt-4">
          {meetings.length === 0 ? (
            <Card className="card-icrcl">
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No meetings yet</p>
                <Button onClick={() => setShowNewDialog(true)} className="mt-4 bg-[#1e3a5f]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Meeting
                </Button>
              </CardContent>
            </Card>
          ) : (
            meetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* New Meeting Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-[#2a9d8f]" />
              Schedule New Meeting
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Meeting Title *</Label>
              <Input
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                placeholder="e.g., Team Standup, Parent Meeting..."
                data-testid="meeting-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                placeholder="Meeting agenda or notes..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newMeeting.scheduled_date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={newMeeting.start_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={newMeeting.end_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, end_time: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select
                value={newMeeting.meeting_type}
                onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff Meeting</SelectItem>
                  <SelectItem value="parent">Parent Meeting</SelectItem>
                  <SelectItem value="team">Team Meeting</SelectItem>
                  <SelectItem value="clinical">Clinical Review</SelectItem>
                  <SelectItem value="training">Training Session</SelectItem>
                  <SelectItem value="individual">Individual / 1-on-1</SelectItem>
                  <SelectItem value="external">External Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Attendee Selection */}
            <div className="space-y-2">
              <Label>Invite Attendees (Optional)</Label>
              <Select
                value=""
                onValueChange={(v) => {
                  if (v && !newMeeting.attendees.includes(v)) {
                    setNewMeeting({ ...newMeeting, attendees: [...newMeeting.attendees, v] });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff to invite..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.filter(s => !newMeeting.attendees.includes(s.id)).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} ({s.role || 'Staff'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {newMeeting.attendees.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newMeeting.attendees.map(attendeeId => {
                    const attendee = staff.find(s => s.id === attendeeId);
                    return attendee ? (
                      <Badge key={attendeeId} variant="secondary" className="flex items-center gap-1">
                        {attendee.first_name} {attendee.last_name}
                        <button 
                          onClick={() => setNewMeeting({
                            ...newMeeting,
                            attendees: newMeeting.attendees.filter(id => id !== attendeeId)
                          })}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button 
              onClick={createMeeting}
              disabled={!newMeeting.title.trim()}
              className="bg-[#1e3a5f]"
              data-testid="create-meeting-btn"
            >
              <Video className="w-4 h-4 mr-2" />
              Create Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-500" />
              Send Meeting Invitation
            </DialogTitle>
          </DialogHeader>
          
          {selectedMeeting && (
            <div className="space-y-4">
              {/* Meeting Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#1e3a5f]">{selectedMeeting.title}</h4>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedMeeting.scheduled_date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedMeeting.start_time} - {selectedMeeting.end_time}
                  </span>
                </div>
                <div className="mt-2">
                  <code className="text-xs bg-white px-2 py-1 rounded border">
                    {window.location.origin}/meeting-room/{selectedMeeting.room_code}
                  </code>
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <Label>Add Email Addresses</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address..."
                    onKeyPress={(e) => e.key === 'Enter' && addEmailToInvite()}
                  />
                  <Button onClick={addEmailToInvite} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Email List */}
              {inviteEmails.length > 0 && (
                <div className="space-y-2">
                  <Label>Recipients ({inviteEmails.length})</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                    {inviteEmails.map((email, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {email}
                        <button 
                          onClick={() => removeEmailFromInvite(email)}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="border-t pt-4">
                <Label className="text-slate-500 text-sm">Or use other methods:</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEmailClient(selectedMeeting)}
                    className="flex-1"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Open Email App
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shareViaWhatsApp(selectedMeeting)}
                    className="flex-1 bg-green-50 hover:bg-green-100 border-green-200"
                  >
                    <MessageSquare className="w-4 h-4 mr-1 text-green-600" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const meetingUrl = `${window.location.origin}/meeting-room/${selectedMeeting.room_code}`;
                      navigator.clipboard.writeText(meetingUrl);
                      toast.success('Link copied!');
                    }}
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy Link
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button 
              onClick={sendMeetingInvites}
              disabled={sendingInvite || inviteEmails.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingInvite ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitations ({inviteEmails.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={showReportsDialog} onOpenChange={setShowReportsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1e3a5f]" />
              Meeting Reports
            </DialogTitle>
          </DialogHeader>
          
          {reports && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{reports.summary?.total_meetings || 0}</p>
                  <p className="text-sm text-blue-700">Total Meetings</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{reports.summary?.completed || 0}</p>
                  <p className="text-sm text-green-700">Completed</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{reports.summary?.with_minutes || 0}</p>
                  <p className="text-sm text-amber-700">With Minutes</p>
                </div>
              </div>

              {/* By Type */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Meetings by Type</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(reports.by_type || {}).map(([type, count]) => (
                    <Badge key={type} variant="outline" className="text-sm">
                      {type.replace('_', ' ')}: {count}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* By Creator */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Meetings by Creator</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {Object.entries(reports.by_creator || {}).map(([creator, count]) => (
                    <div key={creator} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                      <span className="text-sm">{creator}</span>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Participants */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-center">
                  <span className="text-3xl font-bold text-[#1e3a5f]">{reports.summary?.total_participants || 0}</span>
                  <span className="text-slate-600 block text-sm">Total Participants</span>
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Users Dialog - Super Admin Only */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Users className="w-5 h-5" />
              Manage Meeting Access
            </DialogTitle>
          </DialogHeader>
          
          {meetingToManage && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <h4 className="font-semibold">{meetingToManage.title}</h4>
                <p className="text-sm text-slate-500">{meetingToManage.scheduled_date} • {meetingToManage.start_time}</p>
              </div>

              {/* Blocked Users */}
              {meetingParticipants.blocked_users?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-600 mb-2">Blocked Users</h4>
                  <div className="space-y-2">
                    {meetingParticipants.blocked_users.map(user => (
                      <div key={user.id} className="flex items-center justify-between bg-red-50 p-3 rounded-lg">
                        <div>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-xs text-slate-500">{user.email} • {user.role}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => unblockUser(user.id)}
                        >
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Block New User */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Block a User</h4>
                <p className="text-xs text-slate-500 mb-2">Select a user to block from this meeting (including Directors)</p>
                <Select
                  onValueChange={(userId) => {
                    if (userId) blockUser(userId);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user to block..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staff
                      .filter(s => !meetingParticipants.blocked_users?.some(b => b.id === s.id))
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.first_name} {s.last_name} ({s.role || 'Staff'})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Participants Log */}
              {meetingParticipants.participants?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">
                    Participants ({meetingParticipants.total_participants || 0})
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {meetingParticipants.participants.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                        <span>{p.name} ({p.role || 'Staff'})</span>
                        <span className="text-xs text-slate-400">
                          Joined: {new Date(p.joined_at).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
